import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import { axiosGet } from 'connectors/axiosGet'
import { axiosPost } from 'connectors/axiosPost'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { useAlert } from 'context/AlertContext'
import type { ApplicationField, FieldType, LocalField, MarketLanguage } from './FormBuilder.types'

export type FormBuilderState = {
  fields: LocalField[]
  setFields: Dispatch<SetStateAction<LocalField[]>>
  languages: MarketLanguage[]
  isLoading: boolean
  newFieldType: FieldType
  setNewFieldType: (type: FieldType) => void
  addField: () => void
  deleteField: (index: number) => void
  moveField: (index: number, direction: 'up' | 'down') => void
  sync: () => Promise<boolean>
  saveVersionSnapshot: () => Promise<void>
}

/**
 * Stan buildera formularza podniesiony poza komponent, żeby przetrwać zmianę
 * zakładek i być zapisany jednym wspólnym Save marketu (bulk-sync endpoint).
 * Bez marketId (formularz dodawania) hook jest bierny — nie fetchuje.
 */
export const useFormBuilderState = (marketId?: string): FormBuilderState => {
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()

  const [fields, setFields] = useState<LocalField[]>([])
  const [languages, setLanguages] = useState<MarketLanguage[]>([])
  const [newFieldType, setNewFieldType] = useState<FieldType>('short_text')
  const [isLoading, setIsLoading] = useState(false)
  // true dopiero po udanym wczytaniu pól — chroni sync przed wysłaniem
  // pustej listy (skasowaniem pól), gdy fetch nie zdążył/padł.
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!marketId) return
    let cancelled = false

    const load = async (): Promise<void> => {
      setIsLoading(true)
      const [serverFields, langs] = await Promise.all([
        axiosGet<ApplicationField[]>(`markets/${marketId}/application-form/admin`, refreshToken),
        axiosGet<MarketLanguage[]>(`market-languages/${marketId}`, refreshToken),
      ])
      if (cancelled) return

      const languageList = Array.isArray(langs) ? langs : []
      setLanguages(languageList)

      if (Array.isArray(serverFields)) {
        setFields(
          serverFields
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(f => ({
              ...f,
              translations: languageList.map(lang => {
                const ex = f.translations.find(t => t.marketLanguageId === lang.id)
                return {
                  marketLanguageId: lang.id,
                  label: ex?.label ?? '',
                  placeholder: ex?.placeholder ?? '',
                  description: ex?.description ?? '',
                }
              }),
              options: (f.options ?? []).map(opt => ({
                ...opt,
                translations: languageList.map(lang => {
                  const ex = opt.translations?.find(t => t.marketLanguageId === lang.id)
                  return { marketLanguageId: lang.id, label: ex?.label ?? '' }
                }),
              })),
            }))
        )
        setLoaded(true)
      }
      setIsLoading(false)
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [marketId, refreshToken])

  const addField = (): void => {
    const restrictionDefaults =
      newFieldType === 'short_text'
        ? { restrictionType: 'characters' as const, restrictionValue: 255 }
        : newFieldType === 'long_text' || newFieldType === 'file_text'
          ? { restrictionType: 'characters' as const, restrictionValue: 1000 }
          : { restrictionType: null as null, restrictionValue: null as null }

    const newField: LocalField = {
      type: newFieldType,
      formSection: 'contest',
      required: false,
      sortOrder: fields.length,
      isActive: true,
      ...restrictionDefaults,
      translations: languages.map(lang => ({
        marketLanguageId: lang.id,
        label: '',
        placeholder: '',
        description: '',
      })),
      options: [],
    }
    setFields(prev => [...prev, newField])
  }

  // Odroczone usuwanie — tylko lokalnie; bulk-sync usunie brakujące pola po stronie API.
  const deleteField = (index: number): void => {
    setFields(prev => prev.filter((_, i) => i !== index))
  }

  const moveField = (index: number, direction: 'up' | 'down'): void => {
    const swapIdx = direction === 'up' ? index - 1 : index + 1
    if (swapIdx < 0 || swapIdx >= fields.length) return
    setFields(prev => {
      const next = [...prev]
      ;[next[index], next[swapIdx]] = [next[swapIdx], next[index]]
      return next.map((f, i) => ({ ...f, sortOrder: i }))
    })
  }

  const sync = async (): Promise<boolean> => {
    // Nie synchronizuj, dopóki pola nie zostały wczytane — inaczej wysłalibyśmy
    // pustą listę i skasowali istniejące pola po stronie API.
    if (!marketId || !loaded) return true
    const payload = {
      fields: fields.map((f, i) => ({
        id: f.id,
        type: f.type,
        formSection: f.formSection,
        required: f.required,
        isActive: f.isActive,
        sortOrder: i,
        restrictionType: f.restrictionType ?? undefined,
        restrictionValue: f.restrictionValue ?? undefined,
        translations: f.translations.filter(t => t.label.trim()),
        options: f.options.map((o, j) => ({
          id: o.id,
          value: o.value,
          isCorrect: o.isCorrect,
          sortOrder: j,
          translations: o.translations.filter(t => t.label.trim()),
        })),
      })),
    }

    const res = await axiosPost<Record<string, unknown>>(
      token,
      refreshToken,
      `markets/${marketId}/application-form/sync`,
      payload
    )
    if (res !== undefined && res !== null && 'error' in res) {
      changeMessage('Error saving form fields', 'error', () => {})
      return false
    }
    return true
  }

  const saveVersionSnapshot = async (): Promise<void> => {
    if (!marketId) return
    await axiosPost(token, refreshToken, `markets/${marketId}/application-form/versions`, {})
    changeMessage('Version snapshot created', 'success', () => {})
  }

  return {
    fields,
    setFields,
    languages,
    isLoading,
    newFieldType,
    setNewFieldType,
    addField,
    deleteField,
    moveField,
    sync,
    saveVersionSnapshot,
  }
}
